import { Entity, EntityId, TemporalEntityId } from 'entities-next/common'
import {
  buildFundingRound,
  FUNDING_ROUND_CACHE_STORE_ID,
  FundingRound,
  getApiFundingRoundHandlers,
} from 'entities-next/fundingRounds'
import { isEmpty, isNil } from 'lodash'
import { useQuery, useQueryClient } from 'react-query'
import { useList } from 'react-use'
import { getIndexOfItem, replaceObjectInArray } from 'utils-next/arrays'
import { getChangedData } from 'utils-next/forms'

export function useFundingRoundListData(companyId: EntityId) {
  const {
    apiGetFundingRounds,
    apiCreateFundingRound,
    apiUpdateFundingRound,
    apiDeleteFundingRound,
  } = getApiFundingRoundHandlers()
  const queryClient = useQueryClient()
  const { data: initialFundingRounds = [] } = useQuery<FundingRound[]>(
    [companyId, FUNDING_ROUND_CACHE_STORE_ID],
    () => apiGetFundingRounds(companyId),
    {
      suspense: true,
    }
  )

  const [fundingRoundList, { update, push, removeAt }] = useList(initialFundingRounds)

  const updateFundingRoundFromCache = (fundingRound: FundingRound) => {
    queryClient.setQueryData([companyId, FUNDING_ROUND_CACHE_STORE_ID], (cache: FundingRound[] = []) => {
      const index = getIndexOfItem(cache, fundingRound, (a: Entity, b: Entity) => a.id === b.id)
      const updatedList = replaceObjectInArray(cache, index, fundingRound)
      return updatedList
    })
  }

  const deleteFundingRoundFromCache = (fundingRoundId: EntityId) => {
    queryClient.setQueryData([companyId, FUNDING_ROUND_CACHE_STORE_ID], (cache: FundingRound[] = []) =>
      cache.filter(({ id }) => id !== fundingRoundId)
    )
  }

  const updateFundingRound = async (data: FundingRound) => {
    const isNew = data.id === TemporalEntityId.new
    if (isNew) {
      const fundingRound = await apiCreateFundingRound(data)
      update(isSameFundingRound(data), fundingRound)
      updateFundingRoundFromCache(data)
      return fundingRound
    } else {
      const previousData = fundingRoundList.find(isSameFundingRound(data)) as FundingRound
      const { investments, ...fundingRoundInfo } = getChangedData(previousData, data)
      let fundingRound

      if (!isNil(investments)) {
        fundingRound = { ...previousData, investments: data.investments }
      }

      if (!isEmpty(fundingRoundInfo)) {
        const fundingRoundData = await apiUpdateFundingRound(data.id, data)
        fundingRound = { ...fundingRoundData, investments: data.investments }
      }

      if (fundingRound) {
        update(isSameFundingRound(data), fundingRound)
        updateFundingRoundFromCache(data)
        return fundingRound
      } else {
        return previousData
      }
    }
  }

  const addNewFundingRound = () => {
    push(buildFundingRound(companyId))
  }

  const deleteFundingRound = async (data: FundingRound) => {
    const isNew = data.id === TemporalEntityId.new
    if (!isNew) {
      await apiDeleteFundingRound(data.id)
      deleteFundingRoundFromCache(data.id)
    }
    removeAt(fundingRoundList.findIndex(isSameFundingRound(data)))
  }

  return {
    fundingRoundList,
    handlers: {
      addNewFundingRound,
      updateFundingRound,
      deleteFundingRound,
    },
  }
}

function isSameFundingRound(fundingRoundA: FundingRound) {
  return (fundingRoundB: FundingRound) =>
    fundingRoundA.id === fundingRoundB.id &&
    fundingRoundA.createdAt?.toISOString() === fundingRoundB.createdAt?.toISOString()
}
